import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { CREDIT_COSTS, CreditAction } from "@/config/pricing";
import { toast } from "sonner";

interface CreditState {
  balance: number;
  monthlyAllowance: number;
  planTier: string;
  rolloverCap: number;
  isUnlimited: boolean;
  isLoading: boolean;
}

export function useCredits() {
  const { user, subscription } = useAuth();
  const [creditState, setCreditState] = useState<CreditState>({
    balance: 0,
    monthlyAllowance: 0,
    planTier: "starter",
    rolloverCap: 0,
    isUnlimited: false,
    isLoading: true,
  });

  const fetchCredits = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase.functions.invoke("manage-credits", {
        body: { action: "check-balance" },
      });
      if (error) throw error;
      setCreditState({
        balance: data.credits_balance ?? 0,
        monthlyAllowance: data.monthly_allowance ?? 0,
        planTier: data.plan_tier ?? "starter",
        rolloverCap: data.rollover_cap ?? 0,
        isUnlimited: data.is_unlimited ?? false,
        isLoading: false,
      });
    } catch (err) {
      console.error("Failed to fetch credits:", err);
      setCreditState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [user]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits, subscription.subscribed]);

  const hasEnough = (action: CreditAction): boolean => {
    if (creditState.isUnlimited) return true;
    return creditState.balance >= CREDIT_COSTS[action];
  };

  const consume = async (action: CreditAction, description?: string): Promise<boolean> => {
    if (creditState.isUnlimited) return true;
    const cost = CREDIT_COSTS[action];
    
    // Allow free actions through without balance check
    if (cost === 0) return true;
    try {
      const { data, error } = await supabase.functions.invoke("manage-credits", {
        body: { action: "consume", creditAction: action, description },
      });
      if (error) throw error;
      if (!data.success) {
        if (data.requires_upgrade) {
          toast.error(data.message || "Upgrade your plan to use this tool.", {
            action: {
              label: "Upgrade",
              onClick: () => window.location.href = "/checkout",
            },
            duration: 8000,
          });
        } else {
          toast.error(data.message || "Failed to use credits");
        }
        return false;
      }
      if (data.new_balance !== undefined) {
        setCreditState((prev) => ({
          ...prev,
          balance: data.new_balance,
        }));
      }
      return true;
    } catch (err: any) {
      toast.error("Failed to process credits");
      return false;
    }
  };

  const getCost = (action: CreditAction): number => CREDIT_COSTS[action];

  return {
    ...creditState,
    fetchCredits,
    hasEnough,
    consume,
    getCost,
  };
}
